/**
 * Lumiari Agent Chat Widget
 * Embeddable chat widget with Shadow DOM isolation
 */

import {
  WidgetConfig,
  WidgetState,
  Message,
  CartState,
  ChatResponse,
  CartSummaryData,
  CheckoutEmbedData,
  WalletAddCardData,
  OrderStatusData,
  NormalizedChatPayload,
  Product,
  EnvelopeAction,
  EnvelopeMessage,
  ProductCardData,
  ConversationSummary,
} from './types';
import { ApiService } from './services/api';
import { StorageService } from './services/storage';
import { RealtimeService } from './services/realtime';
import { ErrorReporter, resolveDsn } from './services/errorReporting';
import { lumiariDock, LumiariDockHandle } from './dock';
import type { HostTransport, RealtimeEvent } from './types';
import { normalizePlatform } from './services/platform';
import { h, render as preactRender } from 'preact';
import { MessageList } from './components/MessageList';
import { CartSummary } from './components/CartSummary';
import { ShopperLoginPanel } from './components/v2/ShopperLoginPanel';
import type { ActionHandler, WidgetIntent } from './components/types';
import { sanitizeUrl as sharedSanitizeUrl } from './services/sanitize';
import { configureI18n, t } from './i18n';
import { DEFAULT_API_URL, checkoutPresentation, getEmbedHostname } from './services/embedHost';

// Re-export shared sanitizer to delegate the private helper below.
// The private form is retained as `this.sanitizeUrl(...)` for the legacy
// imperative paths that still reference it across this file.
import {
  CHECKOUT_READY,
  CHECKOUT_COMPLETE,
  CHECKOUT_STATUS,
  CHECKOUT_CLOSE,
  OPEN_PAYMENT_URL,
  PREFILL,
  WALLET_READY,
  WALLET_PREFILL,
  CARD_SAVED,
  CARD_ERROR,
  WALLET_CLOSE,
  DEFAULT_CHECKOUT_ORIGIN,
} from './services/postmessage';
import widgetStyles from './styles/widget.css';

// SVG Icons — editorial stroke set (W-1 north-star). Rendered via
// innerHTML into trusted chrome only; colored through `currentColor`.
const SVG = (body: string, sw = '1.7') =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
const ICONS = {
  // Launcher avatar — chat bubble.
  chat: SVG('<path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l1-4.6A8 8 0 1 1 21 12z"/>', '1.8'),
  // History — clock with a counter-clockwise arrow.
  history: SVG('<path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3.5 2"/>'),
  // Dock to the side.
  dock: SVG('<rect x="3" y="4" width="18" height="16" rx="2.6"/><line x1="14.5" y1="4" x2="14.5" y2="20"/>'),
  // Fullscreen — four corners.
  fullscreen: SVG('<path d="M8 3H5.5A2.5 2.5 0 0 0 3 5.5V8"/><path d="M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8"/><path d="M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16"/><path d="M3 16v2.5A2.5 2.5 0 0 0 5.5 21H8"/>'),
  // Minimize — a single soft line (not an aggressive X).
  minimize: SVG('<line x1="6" y1="12" x2="18" y2="12"/>', '1.9'),
  // Back chevron.
  back: SVG('<path d="M15 6l-6 6 6 6"/>', '1.8'),
  // Send — up arrow.
  send: SVG('<line x1="12" y1="19" x2="12" y2="6"/><path d="M6 12l6-6 6 6"/>', '1.9'),
  // Shopping bag.
  cart: SVG('<path d="M6 2 4 6v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6l-2-4H6z"/><path d="M4 6h16"/><path d="M16 10a4 4 0 0 1-8 0"/>'),
  // User — head + shoulders (identity / login entry).
  user: SVG('<path d="M20 21v-1.6a4.4 4.4 0 0 0-4.4-4.4H8.4A4.4 4.4 0 0 0 4 19.4V21"/><circle cx="12" cy="7.5" r="3.7"/>'),
};

function normalizeOrigin(value: string): string {
  try {
    return new URL(value).origin;
  } catch {
    return String(value || '').replace(/\/+$/, '');
  }
}

export class ChatWidget {
  // `config`, `api`, `storage`, `state` and the few methods marked
  // `@internal` below are public ONLY so `widget-host.js` (a separate
  // bundle, entry src/widget-host.ts) can drive the widget through a typed
  // surface (`HostWidget` in src/services/hostTransport.ts). Not a
  // host-page API.
  /** @internal */
  readonly config: Required<Omit<WidgetConfig, 'transport' | 'host' | 'platform'>>;
  private container: HTMLDivElement;
  private shadowRoot: ShadowRoot;
  /** @internal */
  readonly api: ApiService;
  /** @internal */
  readonly storage: StorageService;
  /** @internal */
  state: WidgetState;
  private requiresHuman: boolean = false;
  // Shopper login (email-OTP) state. `shopperUser` non-null ⇒ logged in.
  private shopperUser: { user_id?: string; name?: string | null; email?: string | null } | null = null;
  private loginStep: 'email' | 'code' = 'email';
  private loginEmail = '';
  private loginLoading = false;
  private loginError: string | null = null;
  // Observability (W2-1). Gated by a Sentry DSN; null when not configured.
  private errorReporter: ErrorReporter | null = null;

  // DOM Elements
  private launcher!: HTMLButtonElement;
  private dockHandle?: LumiariDockHandle;
  private scrim!: HTMLDivElement;
  private window!: HTMLDivElement;
  private messagesContainer!: HTMLDivElement;
  private input!: HTMLTextAreaElement;
  private sendButton!: HTMLButtonElement;
  private cartPreview!: HTMLDivElement;
  private cta: HTMLButtonElement | null = null;
  private checkoutQueued = false;
  private checkoutInFlight = false;
  private historyLoaded = false;

  // Iframe state
  private iframeContainer: HTMLDivElement | null = null;
  private currentIframe: HTMLIFrameElement | null = null;
  private iframeMessageHandler: ((event: MessageEvent) => void) | null = null;
  private lastCheckoutEmbed: CheckoutEmbedData | null = null;
  private checkoutPollTimer: ReturnType<typeof setTimeout> | null = null;
  private checkoutPollAbort: AbortController | null = null;
  private checkoutPollFailures = 0;

  // Realtime hub subscription
  private realtime: RealtimeService | null = null;
  private realtimeOrganizationId: string | null = null;
  private realtimeToken: string | null = null;

  // Streaming bubble state. When a `conversation.token_chunk` arrives
  // and there's no in-flight assistant bubble, we create one with the
  // first delta. Subsequent chunks append. `conversation.message_complete`
  // (or the HTTP envelope arriving) closes the bubble — at which point
  // any *additional* envelope messages (product_grid, cart_summary, …)
  // render fresh AFTER the streaming text. The two state fields:
  //
  //   - streamingBubbleEl: the .lc-message bubble that's currently
  //     accumulating chunks. ``null`` when no stream is in flight.
  //   - streamingConvId / streamingTraceId: identifiers used to match
  //     the streamed text against the HTTP envelope so we don't render
  //     it twice when both arrive (the WS race vs the chat HTTP turn).
  //
  // The streaming bubble is intentionally a plain DOM bubble (NOT a
  // typed envelope message) — it's always plain text from the LLM.
  // Structured envelope contents (product_grid, cart, …) come through
  // the regular envelope_messages path and are rendered alongside.
  private streamingBubbleEl: HTMLDivElement | null = null;
  private streamingTextEl: HTMLDivElement | null = null;
  private streamingText: string = '';
  private streamingCompleted = false;

  // Agent opening greeting (A3b). The agent always greets first: on the
  // first open of an empty conversation the widget fetches
  // GET /api/agents/{agentId}/opening and renders the returned message(s)
  // as the first assistant bubble(s). Guarded to fire at most once per
  // session so reopening never re-greets or duplicates.
  private openingRequested = false;

  constructor(config: WidgetConfig, tx?: HostTransport) {
    const storageScope = config.storeId || config.widgetKey || 'lumiari_widget';
    // Configure i18n first so the built-in text defaults below resolve in the
    // host's locale. Explicit `texts` overrides still take precedence.
    const locale = configureI18n(config.locale);
    // Merge with defaults
    this.config = {
      locale,
      storeId: storageScope,
      agentId: config.agentId || 'default',
      widgetKey: config.widgetKey || '',
      // Storefront-forwarded shopper identity. Threaded to ApiService and
      // sent as X-Lumiari-Shopper-Token ONLY on the bootstrap request.
      // Bearer secret — never logged.
      shopperToken: config.shopperToken || '',
      apiUrl: config.apiUrl || DEFAULT_API_URL,
      wsUrl: config.wsUrl || '',
      realtimeUrl: config.realtimeUrl || '',
      checkoutOrigin: normalizeOrigin(config.checkoutOrigin || DEFAULT_CHECKOUT_ORIGIN),
      position: config.position || 'bottom-right',
      theme: {
        // Lumiari Agent (blue #3B82F6) — ver lumiari-design-system
        primaryColor: config.theme?.primaryColor || '#3B82F6',
        secondaryColor: config.theme?.secondaryColor || '#6366F1',
        fontFamily: config.theme?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderRadius: config.theme?.borderRadius || '16px',
        // Per-brand WhatsApp palette overrides (empty = keep WhatsApp default).
        headerBg: config.theme?.headerBg || '',
        headerText: config.theme?.headerText || '',
        sentBubbleColor: config.theme?.sentBubbleColor || '',
        accentColor: config.theme?.accentColor || '',
        wallpaperColor: config.theme?.wallpaperColor || '',
      },
      features: {
        cart: true,
        checkout: true,
        fileUpload: false,
        voice: false,
        productCards: true,
        quickReplies: true,
        ...config.features,
      },
      texts: {
        headerTitle: config.texts?.headerTitle || t('header.title'),
        headerSubtitle: config.texts?.headerSubtitle || t('header.subtitle'),
        inputPlaceholder: config.texts?.inputPlaceholder || t('composer.placeholder'),
        sendButton: config.texts?.sendButton || t('composer.send'),
        welcomeMessage: config.texts?.welcomeMessage || t('composer.welcome'),
      },
      onReady: config.onReady || (() => {}),
      onMessage: config.onMessage || (() => {}),
      onCartUpdate: config.onCartUpdate || (() => {}),
      onCheckout: config.onCheckout || (() => {}),
      onError: config.onError || (() => {}),
      errorReporting: config.errorReporting || {},
    };

    // Observability (W2-1). Resolve a DSN from config / embed attribute /
    // global; only when present do we lazily load Sentry. No DSN ⇒ the
    // reporter stays null and contributes zero runtime cost. The SDK is
    // never statically bundled (see services/errorReporting.ts), so this
    // does not affect the dist/widget.js size budget.
    this.initErrorReporting();

    // Initialize services
    this.api = new ApiService(
      this.config.apiUrl,
      this.config.storeId,
      this.config.agentId,
      this.config.widgetKey ? this.config.widgetKey : null,
      this.config.shopperToken ? this.config.shopperToken : null,
    );
    // Host transport (playground): plugged in BEFORE anything below can
    // issue a request. Undefined for every storefront ⇒ plain HTTP.
    this.api.tx = tx;
    const platform = normalizePlatform(config.platform);
    if (platform) this.api.q = `?platform=${platform}`;
    this.storage = new StorageService(
      this.config.storeId,
      this.config.widgetKey || null
    );
    const restoredConversationId = this.storage.getConversationId();
    if (restoredConversationId) {
      this.api.setConversationId(restoredConversationId);
    }

    // Initialize state
    this.state = {
      isOpen: false,
      isMinimized: false,
      isLoading: false,
      isExpanded: false,
      isDocked: false,
      isFullscreen: false,
      showHistory: false,
      showCartPanel: false,
      sessionId: this.storage.getSessionId(),
      messages: this.storage.getMessages(),
      cart: { items: [], total: 0, itemCount: 0 },
      user: this.storage.getUser(),
      phase: 'welcome',
      unreadCount: 0,
    };

    // Create container with Shadow DOM. Host-element id renamed in
    // v3.0.0 (breaking — merchant CSS targeting the pre-v3 id must
    // update; see MIGRATION_V2.md).
    this.container = document.createElement('div');
    this.container.id = 'lumiari-chat-widget';
    this.shadowRoot = this.container.attachShadow({ mode: 'closed' });

    // Inject styles
    this.injectStyles();

    // Build UI
    this.buildUI();

    // Add to DOM
    document.body.appendChild(this.container);

    // Restore state
    const savedState = this.storage.getWidgetState();
    if (savedState?.isOpen) {
      this.open();
    }

    // Add welcome message if no messages. This is a synthetic, instant
    // greeting (id `welcome`). When a widget key is configured the agent
    // greets first via the server-side opening endpoint (fetched on first
    // open — see `maybeFetchOpening`); that path REPLACES this stub so the
    // shopper sees the real agent greeting rather than two greetings.
    if (this.state.messages.length === 0) {
      this.addMessage({
        id: 'welcome',
        role: 'assistant',
        content: this.config.texts.welcomeMessage,
        timestamp: new Date(),
        type: 'text',
      });
    }

    // Notify ready
    this.config.onReady();
  }

  private injectStyles(): void {
    const style = document.createElement('style');

    // White-label overrides. The single brand accent (--lc-primary) drives
    // the derived press/tint via color-mix in the stylesheet, so hosts only
    // supply ONE colour (feed it from @lumiari/tokens if desired; default is
    // Agent blue #3B82F6). The font is inheritable from the host site — pass
    // theme.fontFamily:'inherit' for full white-label typography.
    //
    // IMPORTANT: appended AFTER the stylesheet so the injected accent wins
    // over the :host default (same specificity → later declaration wins).
    const overrides: string[] = [];
    if (this.config.theme.primaryColor) {
      overrides.push(`--lc-primary: ${this.config.theme.primaryColor};`);
    }
    if (this.config.theme.fontFamily) {
      overrides.push(`--lc-font: ${this.config.theme.fontFamily};`);
    }
    const customVars = overrides.length
      ? `\n:host { ${overrides.join(' ')} }\n`
      : '';

    style.textContent = widgetStyles + customVars;
    this.shadowRoot.appendChild(style);
  }

  private buildUI(): void {
    // Scrim — dims the storefront behind the sheet. Clicking it minimizes
    // softly back to the launcher (non-destructive).
    this.scrim = document.createElement('div');
    this.scrim.className = 'lc-scrim';
    this.scrim.setAttribute('aria-hidden', 'true');
    this.scrim.addEventListener('click', () => this.minimize());
    this.shadowRoot.appendChild(this.scrim);

    // Create launcher — an editorial pill (avatar + label). Shown when the
    // sheet is closed or minimized.
    this.launcher = document.createElement('button');
    this.launcher.className = `lc-launcher ${this.config.position === 'bottom-left' ? 'left' : ''}`;
    this.launcher.innerHTML = `<span class="lc-launcher-avatar">${ICONS.chat}</span><span class="lc-launcher-label">${this.escapeHtml(this.config.texts.headerTitle)}</span>`;
    this.launcher.setAttribute('aria-label', 'Abrir asistente');
    this.launcher.setAttribute('aria-expanded', 'false');
    this.launcher.onclick = () => this.toggle();
    this.shadowRoot.appendChild(this.launcher);

    // Coexistence: register with the shared floating-launcher dock so this
    // launcher tiles with any sibling Lumiari widget (the live-widget) in the
    // same corner instead of overlapping. `order:20` stacks the chat above the
    // live launcher (order:10). Symmetric + count-agnostic — replaces the old
    // one-directional sniff-and-lift hack.
    this.dockHandle = lumiariDock.register({
      id: 'lumiari-chat',
      el: this.launcher,
      corner: this.config.position || 'bottom-right',
      order: 20,
    });

    // Create chat window (the "sheet")
    this.window = document.createElement('div');
    this.window.className = `lc-window ${this.config.position === 'bottom-left' ? 'left' : ''}`;
    this.window.setAttribute('role', 'dialog');
    this.window.setAttribute('aria-modal', 'true');
    this.window.setAttribute('aria-label', this.config.texts.headerTitle);
    this.window.innerHTML = this.getWindowHTML();
    this.shadowRoot.appendChild(this.window);

    // Get references
    this.messagesContainer = this.window.querySelector('.lc-messages') as HTMLDivElement;
    this.input = this.window.querySelector('.lc-input') as HTMLTextAreaElement;
    this.sendButton = this.window.querySelector('.lc-send-btn') as HTMLButtonElement;
    this.cartPreview = this.window.querySelector('.lc-cart-preview') as HTMLDivElement;

    // Attach event listeners
    this.attachEventListeners();

    // Restore logged-in shopper state from a persisted session (reload).
    this.restoreShopperSession();

    // Render existing messages
    this.renderMessages();
  }

  private getWindowHTML(): string {
    return `
      <div class="lc-header">
        <div class="lc-header-inner">
        <div class="lc-header-group">
          <button class="lc-header-history lc-iconbtn" title="Historial" aria-label="Historial de conversaciones">${ICONS.history}</button>
          ${this.config.features.cart ? `
          <button class="lc-header-cart lc-iconbtn" title="Mi carrito" aria-label="Mi carrito">${ICONS.cart}<span class="lc-header-badge lc-header-cart-badge" hidden>0</span></button>
          ` : ''}
          <button class="lc-header-user lc-iconbtn" title="Iniciar sesión / mi cuenta" aria-label="Iniciar sesión o ver mi cuenta">${ICONS.user}</button>
        </div>
        <div class="lc-header-group">
          <button class="lc-header-dock lc-iconbtn" title="Acoplar al lateral" aria-label="Acoplar al lateral" aria-pressed="false">${ICONS.dock}</button>
          <button class="lc-header-full lc-iconbtn" title="Pantalla completa" aria-label="Pantalla completa" aria-pressed="false">${ICONS.fullscreen}</button>
          <button class="lc-header-min lc-iconbtn" title="Minimizar" aria-label="Minimizar">${ICONS.minimize}</button>
        </div>
        </div>
      </div>

      <div class="lc-messages" role="log" aria-live="polite"></div>

      ${this.config.features.cart ? `
        <div class="lc-cart-preview" style="display: none;">
          <div class="lc-cart-info" role="button" tabindex="0">
            <div class="lc-cart-icon">
              ${ICONS.cart}
              <span class="lc-cart-count">0</span>
            </div>
            <span class="lc-cart-total">$0</span>
            <span class="lc-cart-view-label">Ver carrito</span>
          </div>
          <button class="lc-cart-btn">Finalizar compra</button>
        </div>
      ` : ''}

      <!-- Cart Panel Overlay -->
      ${this.config.features.cart ? `
        <div class="lc-cart-panel" style="display: none;">
          <div class="lc-cart-panel-header">
            <button class="lc-cart-back" aria-label="Volver al chat">${ICONS.back}</button>
            <h3>Mi Carrito</h3>
            <span class="lc-cart-panel-count">0 items</span>
          </div>
          <div class="lc-cart-panel-items"></div>
          <div class="lc-cart-panel-footer">
            <div class="lc-cart-panel-totals">
              <div class="lc-cart-panel-subtotal">
                <span>Subtotal</span>
                <span class="lc-cart-panel-subtotal-value">$0</span>
              </div>
              <div class="lc-cart-panel-total">
                <span>Total</span>
                <span class="lc-cart-panel-total-value">$0</span>
              </div>
            </div>
          </div>
        </div>
      ` : ''}

      <div class="lc-foot">
        <div class="lc-input-area">
          <div class="lc-input-wrapper">
            <textarea
              class="lc-input"
              placeholder="${this.escapeAttribute(this.config.texts.inputPlaceholder)}"
              rows="1"
            ></textarea>
          </div>
          <button class="lc-send-btn" aria-label="${this.escapeAttribute(this.config.texts.sendButton)}">${ICONS.send}</button>
        </div>
        <p class="lc-disclaimer">Asistido por <b>${this.escapeHtml(this.config.texts.headerTitle)}</b> · las respuestas pueden contener errores</p>
      </div>

      <!-- History slide-over -->
      <div class="lc-history" aria-hidden="true">
        <div class="lc-history-header">
          <button class="lc-history-back lc-iconbtn" aria-label="Volver">${ICONS.back}</button>
          <span class="lc-history-title">Conversaciones</span>
        </div>
        <div class="lc-history-list" role="list"></div>
      </div>

      <!-- Login / account slide-over -->
      <div class="lc-login" aria-hidden="true">
        <div class="lc-login-header">
          <button class="lc-login-back lc-iconbtn" aria-label="Volver">${ICONS.back}</button>
          <span class="lc-login-title">Mi cuenta</span>
        </div>
        <div class="lc-login-body"></div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    // History slide-over
    this.window.querySelector('.lc-header-history')
      ?.addEventListener('click', () => this.toggleHistory(true));
    this.window.querySelector('.lc-history-back')
      ?.addEventListener('click', () => this.toggleHistory(false));

    // Header cart + user icons (next to history).
    this.window.querySelector('.lc-header-cart')
      ?.addEventListener('click', () => this.showCartPanel());
    this.window.querySelector('.lc-header-user')
      ?.addEventListener('click', () => this.toggleLogin(true));
    this.window.querySelector('.lc-login-back')
      ?.addEventListener('click', () => this.toggleLogin(false));

    // Mode toggles: dock / fullscreen / minimize.
    this.window.querySelector('.lc-header-dock')
      ?.addEventListener('click', () => this.toggleDock());
    this.window.querySelector('.lc-header-full')
      ?.addEventListener('click', () => this.toggleFullscreen());
    this.window.querySelector('.lc-header-min')
      ?.addEventListener('click', () => this.minimize());

    // Send button
    this.sendButton.addEventListener('click', () => this.sendMessage());

    // Input - Enter to send
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    // Dialog keyboard behaviour: Escape softly minimizes (history first),
    // Tab is trapped inside the sheet while it is the modal surface.
    this.window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen && !this.state.isMinimized) {
        e.preventDefault();
        if (this.state.showHistory) {
          this.toggleHistory(false);
        } else {
          this.minimize();
        }
        return;
      }
      if (e.key === 'Tab' && this.state.isOpen && !this.state.isMinimized) {
        this.trapFocus(e);
      }
    });

    // Auto-resize input
    this.input.addEventListener('input', () => {
      this.input.style.height = 'auto';
      this.input.style.height = Math.min(this.input.scrollHeight, 100) + 'px';
    });

    // Cart preview - click to view cart
    const cartInfo = this.cartPreview?.querySelector('.lc-cart-info');
    cartInfo?.addEventListener('click', () => this.viewCart());

    // Cart button - checkout
    // ONE checkout CTA, moved between the cart bar and the open cart panel
    // (see moveCta): the DOM never carries a second, hidden "Finalizar compra".
    this.cta = this.cartPreview?.querySelector('.lc-cart-btn') as HTMLButtonElement | null;
    this.cta?.addEventListener('click', () => {
      if (this.state.showCartPanel) this.hideCartPanel();
      this.initiateCheckout();
    });

    // Cart panel - back button
    const cartBack = this.window.querySelector('.lc-cart-back');
    cartBack?.addEventListener('click', () => this.hideCartPanel());
  }

  // =========================================
  // PUBLIC METHODS
  // =========================================

  public open(): void {
    this.state.isOpen = true;
    this.state.isMinimized = false;
    this.state.unreadCount = 0;
    this.window.classList.add('open');
    this.window.classList.remove('is-min');
    this.scrim.classList.add('open');
    this.applyLauncherState();
    this.updateBadge();
    this.storage.setWidgetState({ isOpen: true });
    // Agent greets first: fetch + render the opening on first open of an
    // empty conversation. Fire-and-forget; fully fail-safe.
    void this.maybeFetchOpening();
    this.scrollToBottom();
    this.input.focus();
    // Attempt realtime subscription — no-op until conversation/cart ids exist.
    this.ensureRealtime();
  }

  /**
   * Soft-minimize: collapse the sheet back to the launcher pill without
   * tearing anything down (realtime + conversation stay live). This is the
   * only in-sheet dismiss affordance — there is no aggressive "X".
   */
  public minimize(): void {
    if (!this.state.isOpen) return;
    this.state.isMinimized = true;
    this.window.classList.add('is-min');
    this.window.classList.remove('show-history');
    this.state.showHistory = false;
    this.scrim.classList.remove('open');
    this.applyLauncherState();
    // Minimize is a DISMISS: persist CLOSED so a page reload restores the
    // launcher pill, not the open sheet. The sheet stays live in-session
    // (realtime + conversation untouched); only the persisted boot state
    // changes. Fixes the reload auto-reopen after minimizing.
    this.storage.setWidgetState({ isOpen: false });
    this.launcher.focus();
  }

  /** Restore the sheet from the minimized launcher pill. */
  public restore(): void {
    this.state.isMinimized = false;
    this.window.classList.remove('is-min');
    this.scrim.classList.add('open');
    this.applyLauncherState();
    this.scrollToBottom();
    this.input.focus();
    this.ensureRealtime();
  }

  public close(): void {
    this.state.isOpen = false;
    this.state.isMinimized = false;
    this.state.showHistory = false;
    this.window.classList.remove('open', 'is-min', 'show-history');
    this.scrim.classList.remove('open');
    this.applyLauncherState();
    this.storage.setWidgetState({ isOpen: false });
    this.teardownRealtime();
  }

  /** Toggle the right-edge dock mode (mutually exclusive with fullscreen). */
  public toggleDock(): void {
    this.state.isFullscreen = false;
    this.state.isDocked = !this.state.isDocked;
    this.syncModes();
    this.scrollToBottom();
  }

  /** Toggle fullscreen (wide centered) mode (mutually exclusive with dock). */
  public toggleFullscreen(): void {
    this.state.isDocked = false;
    this.state.isFullscreen = !this.state.isFullscreen;
    this.syncModes();
    this.scrollToBottom();
  }

  /** Backward-compatible alias kept for the public API surface. */
  public toggleExpand(): void {
    this.toggleFullscreen();
  }

  /** Open/close the conversation history slide-over. */
  public toggleHistory(show: boolean): void {
    this.state.showHistory = show;
    this.window.classList.toggle('show-history', show);
    const historyEl = this.window.querySelector('.lc-history');
    historyEl?.setAttribute('aria-hidden', show ? 'false' : 'true');
    const historyBtn = this.window.querySelector('.lc-header-history');
    historyBtn?.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (show) {
      this.loadHistory();
      const back = this.window.querySelector('.lc-history-back') as HTMLElement | null;
      back?.focus();
    }
  }

  /** Reflect the current mode flags onto the sheet + scrim classes + aria. */
  private syncModes(): void {
    this.window.classList.toggle('is-dock', this.state.isDocked);
    this.window.classList.toggle('is-full', this.state.isFullscreen);
    this.scrim.classList.toggle('dock', this.state.isDocked);
    const dockBtn = this.window.querySelector('.lc-header-dock');
    const fullBtn = this.window.querySelector('.lc-header-full');
    dockBtn?.classList.toggle('lc-iconbtn--active', this.state.isDocked);
    dockBtn?.setAttribute('aria-pressed', this.state.isDocked ? 'true' : 'false');
    fullBtn?.classList.toggle('lc-iconbtn--active', this.state.isFullscreen);
    fullBtn?.setAttribute('aria-pressed', this.state.isFullscreen ? 'true' : 'false');
  }

  /** Launcher label/visibility depending on closed vs minimized state. */
  private applyLauncherState(): void {
    const visible = !this.state.isOpen || this.state.isMinimized;
    this.launcher.classList.toggle('hidden', !visible);
    // Free this launcher's dock slot while it's hidden so a sibling launcher
    // (the live-widget) reflows into it, and reclaim it when shown again.
    this.dockHandle?.setHidden(!visible);
    const label = this.launcher.querySelector('.lc-launcher-label');
    if (label) {
      label.textContent = this.state.isMinimized
        ? 'Seguir la charla'
        : this.config.texts.headerTitle;
    }
    this.launcher.classList.toggle('lc-launcher--resume', this.state.isMinimized);
    this.launcher.setAttribute('aria-expanded', this.state.isOpen && !this.state.isMinimized ? 'true' : 'false');
    this.launcher.setAttribute(
      'aria-label',
      this.state.isMinimized ? 'Seguir la charla' : 'Abrir asistente',
    );
  }

  /** Focusable elements within the sheet, in DOM order. */
  private getFocusable(): HTMLElement[] {
    const nodes = this.window.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    return Array.from(nodes).filter((el) => {
      // Skip elements inside the hidden history panel when it is closed.
      if (!this.state.showHistory && el.closest('.lc-history')) return false;
      if (this.state.showHistory && !el.closest('.lc-history') && !el.closest('.lc-header')) {
        return false;
      }
      return el.offsetParent !== null || el === this.input;
    });
  }

  /** Keep Tab focus inside the sheet while it is the modal surface. */
  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.shadowRoot.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !active || !this.window.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  public showCartPanel(): void {
    this.state.showCartPanel = true;
    const panel = this.window.querySelector('.lc-cart-panel') as HTMLElement;
    if (panel) {
      panel.style.display = 'flex';
      this.moveCta('lc-cart-panel-checkout', '.lc-cart-panel-footer');
      this.renderCartPanel();
    }
  }

  private moveCta(cls: string, host: string): void {
    // The panel (the only caller) exists only with features.cart, like the CTA.
    this.cta!.className = cls;
    this.window.querySelector(host)!.appendChild(this.cta!);
  }

  private viewCart(): void {
    this.showCartPanel();
    this.dispatchToolIntent({
      tool: 'cart.view',
      args: { cart_id: this.storage.getCartId() || undefined },
      args_schema_ref: 'cart.view',
    });
  }

  public hideCartPanel(): void {
    this.state.showCartPanel = false;
    const panel = this.window.querySelector('.lc-cart-panel') as HTMLElement;
    if (panel) {
      panel.style.display = 'none';
      this.moveCta('lc-cart-btn', '.lc-cart-preview');
    }
  }

  // =========================================
  // Shopper login (email-OTP) — the user icon
  // =========================================

  /** Restore the logged-in state from a persisted session on boot. */
  private restoreShopperSession(): void {
    const uid = this.api.getSessionUserId();
    if (uid) {
      this.shopperUser = { user_id: uid };
      this.updateUserIcon();
    }
  }

  public toggleLogin(show: boolean): void {
    this.window.classList.toggle('show-login', show);
    const el = this.window.querySelector('.lc-login');
    el?.setAttribute('aria-hidden', show ? 'false' : 'true');
    const btn = this.window.querySelector('.lc-header-user');
    btn?.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (show) {
      this.renderLoginPanel();
      const back = this.window.querySelector('.lc-login-back') as HTMLElement | null;
      back?.focus();
    }
  }

  /** Mount the OTP form (anonymous) or the account view (logged in). */
  private renderLoginPanel(): void {
    const host = this.window.querySelector('.lc-login-body') as HTMLElement | null;
    if (!host) return;
    const titleEl = this.window.querySelector('.lc-login-title');
    if (titleEl) titleEl.textContent = this.shopperUser ? 'Mi cuenta' : 'Iniciar sesión';

    if (this.shopperUser) {
      preactRender(null, host); // unmount any Preact form first
      const who = this.shopperUser.name || this.shopperUser.email || 'tu cuenta';
      host.innerHTML = `
        <div class="lc-login-account">
          <p class="lc-login-account-hello">Sesión iniciada como <b class="lc-login-account-name"></b></p>
          <button class="lc-login-orders lc-login-btn lc-login-btn--primary">Mis pedidos</button>
          <button class="lc-login-logout lc-login-btn lc-login-btn--secondary">Cerrar sesión</button>
        </div>`;
      const nameEl = host.querySelector('.lc-login-account-name');
      if (nameEl) nameEl.textContent = who; // textContent → never inject
      host.querySelector('.lc-login-orders')?.addEventListener('click', () => {
        this.toggleLogin(false);
        void this.sendMessage('Quiero ver mis pedidos');
      });
      host.querySelector('.lc-login-logout')?.addEventListener('click', () => this.logoutShopper());
      return;
    }

    host.innerHTML = '';
    preactRender(
      h(ShopperLoginPanel as any, {
        onDispatch: (intent: { tool?: string; args?: Record<string, unknown> }) =>
          void this.handleLoginDispatch(intent),
        initialEmail: this.loginEmail || undefined,
        step: this.loginStep,
        loading: this.loginLoading,
        errorMessage: this.loginError || undefined,
      }),
      host,
    );
  }

  /** Route the panel's tool dispatches to the OTP proxy (not the chat endpoint). */
  private async handleLoginDispatch(intent: {
    tool?: string;
    args?: Record<string, unknown>;
  }): Promise<void> {
    const args = intent.args || {};
    if (intent.tool === 'auth.shopper_login.v1') {
      const email = String(args.email || '').trim();
      this.loginEmail = email;
      this.loginLoading = true;
      this.loginError = null;
      this.renderLoginPanel();
      const res = await this.api.shopperLoginStart(email);
      this.loginLoading = false;
      const data = res.data as { sent?: boolean; reason?: string | null } | undefined;
      if (res.success && data?.reason === 'rate_limit') {
        this.loginError = 'Pediste demasiados códigos. Esperá unos minutos.';
      } else if (!res.success) {
        this.loginError = 'No pudimos enviar el código. Probá de nuevo.';
      } else {
        this.loginStep = 'code';
      }
      this.renderLoginPanel();
      return;
    }
    if (intent.tool === 'auth.shopper_verify.v1') {
      const email = String(args.email || this.loginEmail || '').trim();
      const code = String(args.code || '').trim();
      this.loginLoading = true;
      this.loginError = null;
      this.renderLoginPanel();
      const res = await this.api.shopperLoginVerify(email, code);
      this.loginLoading = false;
      const data = res.data as
        | { success?: boolean; user?: { user_id?: string; name?: string | null; email?: string | null }; reason?: string }
        | undefined;
      if (res.success && data?.success) {
        // Logged in — the rotated widget-session (with the sealed user_id) was
        // stored by the api layer automatically. Reflect the account state.
        this.shopperUser = data.user || { email };
        this.loginStep = 'email';
        this.loginEmail = '';
        this.loginError = null;
        this.updateUserIcon();
      } else {
        this.loginError = this.loginErrorFor(data?.reason);
      }
      this.renderLoginPanel();
      return;
    }
  }

  private loginErrorFor(reason?: string): string {
    switch (reason) {
      case 'expired':
        return 'El código expiró. Pedí uno nuevo.';
      case 'too_many_attempts':
        return 'Demasiados intentos. Pedí un código nuevo.';
      case 'not_found':
        return 'No encontramos ese código. Revisá el email.';
      default:
        return 'Código inválido. Revisá los dígitos e intentá de nuevo.';
    }
  }

  private logoutShopper(): void {
    this.api.logout();
    this.shopperUser = null;
    this.loginStep = 'email';
    this.loginEmail = '';
    this.loginError = null;
    this.updateUserIcon();
    this.renderLoginPanel();
  }

  /** Reflect logged-in state on the header user icon. */
  private updateUserIcon(): void {
    const btn = this.window.querySelector('.lc-header-user');
    if (!btn) return;
    const loggedIn = !!this.shopperUser;
    btn.classList.toggle('is-authed', loggedIn);
    btn.setAttribute('title', loggedIn ? 'Mi cuenta' : 'Iniciar sesión / mi cuenta');
    btn.setAttribute(
      'aria-label',
      loggedIn ? 'Mi cuenta (sesión iniciada)' : 'Iniciar sesión o ver mi cuenta',
    );
  }

  /**
   * Render the cart panel via the v1 Preact CartSummary component.
   * The panel structure (header / items / footer) is provided by the
   * Twig-style window HTML; we mount Preact only into the items
   * container, and update header counts + footer totals imperatively
   * so we don't fight the legacy DOM.
   */
  private renderCartPanel(): void {
    const panel = this.window.querySelector('.lc-cart-panel');
    if (!panel) return;

    const itemsContainer = panel.querySelector('.lc-cart-panel-items') as HTMLElement | null;
    const countEl = panel.querySelector('.lc-cart-panel-count');
    const subtotalEl = panel.querySelector('.lc-cart-panel-subtotal-value');
    const totalEl = panel.querySelector('.lc-cart-panel-total-value');
    if (!itemsContainer) return;

    const itemCount = this.state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
    if (countEl) {
      countEl.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    }
    const totalStr = `$${this.state.cart.total.toLocaleString('es-AR')}`;
    if (subtotalEl) subtotalEl.textContent = totalStr;
    if (totalEl) totalEl.textContent = totalStr;

    // Map internal CartState into a CartSummaryData shape so we can
    // reuse the CartSummary Preact component (which already handles
    // empty state, image sanitization, and structured intents).
    const cart: CartSummaryData = {
      cart_id: this.storage.getCartId() || '',
      items: this.state.cart.items.map((it) => ({
        product_id: it.id,
        name: it.name,
        unit_price: it.price,
        quantity: it.quantity,
        image_url: it.image || '',
        currency: 'ARS',
      })),
      totals: { subtotal: this.state.cart.total, total: this.state.cart.total },
    };
    const onAction: ActionHandler = (intent) => this.handleWidgetIntent(intent);
    preactRender(
      h(CartSummary as any, { cart, onAction, showCheckout: false }),
      itemsContainer,
    );
  }

  public toggle(): void {
    if (this.state.isMinimized) {
      this.restore();
    } else if (this.state.isOpen) {
      this.minimize();
    } else {
      this.open();
    }
  }

  // =========================================
  // HISTORY (conversation slide-over)
  // =========================================

  /**
   * Fill the history slide-over with the CURRENT conversation.
   *
   * There is no shopper-scoped conversation listing in assistant-ms (the
   * route this used to call, `GET /api/agents/{id}/conversations`, never
   * existed and 404'd in production), so the panel always showed the current
   * conversation. VESNA-8 (2026-10-03) removed the dead call; the screen is
   * unchanged. Loaded once per session.
   */
  private loadHistory(): void {
    if (this.historyLoaded) return;
    this.historyLoaded = true;
    let items: ConversationSummary[] = [];
    const convId = this.api.getConversationId() || this.storage.getConversationId();
    if (convId) {
      const firstUser = this.state.messages.find((m) => m.role === 'user');
      items = [
        {
          conversation_id: convId,
          title: firstUser?.content?.slice(0, 60) || 'Conversación actual',
          time_label: 'Ahora',
          message_count: this.state.messages.length,
        },
      ];
    }
    this.renderHistory(items);
  }

  private renderHistory(items: ConversationSummary[]): void {
    const list = this.window.querySelector('.lc-history-list');
    if (!list) return;
    list.textContent = '';
    if (items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'lc-history-empty';
      empty.textContent = 'Todavía no tenés conversaciones anteriores.';
      list.appendChild(empty);
      return;
    }
    items.forEach((item) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'lc-history-row';
      row.setAttribute('role', 'listitem');
      row.dataset.conversationId = item.conversation_id;

      const title = document.createElement('div');
      title.className = 'lc-history-row-title';
      title.textContent = item.title || 'Conversación';
      row.appendChild(title);

      const meta = document.createElement('div');
      meta.className = 'lc-history-row-meta';
      const parts: string[] = [];
      if (item.time_label) parts.push(item.time_label);
      if (typeof item.message_count === 'number') {
        parts.push(`${item.message_count} mensajes`);
      } else if (item.status_label) {
        parts.push(item.status_label);
      }
      meta.textContent = parts.join(' · ');
      row.appendChild(meta);

      row.addEventListener('click', () => {
        // Selecting the current conversation simply returns to the thread.
        this.toggleHistory(false);
        this.input.focus();
      });
      list.appendChild(row);
    });
  }

  public async sendMessage(text?: string): Promise<void> {
    const message = text || this.input.value.trim();
    if (!message || this.state.isLoading) return;

    // Clear input
    this.input.value = '';
    this.input.style.height = 'auto';

    // Add user message
    const userMessage: Message = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date(),
      type: 'text',
    };
    this.addMessage(userMessage);

    // Show typing indicator
    this.showTyping();

    try {
      this.state.isLoading = true;

      // Send to API
      const response = await this.api.sendMessage(message, this.state.sessionId);

      // Hide typing
      this.hideTyping();

      if (response.success && response.data) {
        const data = response.data;

        // Persist server-issued conversation_id (envelope or legacy)
        if (data.conversation_id) {
          this.storage.setConversationId(data.conversation_id);
          this.state.phase = data.phase || this.state.phase;
        }

        // Update session ID (legacy field)
        if (data.session_id) {
          this.state.sessionId = data.session_id;
          this.storage.setSessionId(data.session_id);
        }

        // requires_human banner
        this.applyRequiresHuman(!!data.requires_human);

        // ---- Envelope-only structured payloads -----------------------
        if (data.cart) {
          this.updateCartFromEnvelope(data.cart);
        } else if (data.cart_items && data.cart_items.length > 0) {
          // Legacy path
          this.updateCart({
            items: data.cart_items,
            total: data.cart_total || 0,
            itemCount: data.cart_items.length,
          });
        }

        if (data.checkout_embed) {
          this.lastCheckoutEmbed = data.checkout_embed;
          this.openCheckoutIframe(data.checkout_embed);
        }

        // Server asked the shopper to identify (email-OTP) to unlock orders /
        // identity data → open the login panel (unless already logged in).
        if (data.login_request && !this.shopperUser) {
          this.toggleLogin(true);
        }

        if (data.wallet_add_card) {
          this.openWalletIframe(data.wallet_add_card);
        }

        // Capture organization_id when surfaced (e.g. wallet_add_card payload).
        // Used as the tenant scope for realtime_hub subscriptions.
        if (data.wallet_add_card && (data.wallet_add_card as any).organization_id) {
          this.realtimeOrganizationId = String(
            (data.wallet_add_card as any).organization_id,
          );
        }
        this.applyRealtimeToken(data.realtime_token);

        // ---- Render assistant turn ----------------------------------
        // Three paths:
        //   - Streaming completed (WS bubble already drew the text):
        //     skip the leading text envelope message but still render
        //     structured contents (product_grid, cart_summary, ...).
        //   - Typed envelope (is_envelope=true with envelope_messages):
        //     route through Preact MessageList for component-based rendering.
        //   - Legacy markdown: keep the imperative DOM path so the existing
        //     markdown extraction + product carousel fallback still works.
        const streamingAlreadyRendered = this.consumeStreamingCompletion();

        const assistantMessage: Message = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          type: data.response_type || 'text',
          options: data.response_options,
          products: data.products || [],  // Include products for carousel
          metadata:
            data.order_status || data.error_code
              ? {
                  order_status: data.order_status,
                  error_code: data.error_code,
                  retry_after_seconds: data.retry_after_seconds,
                }
              : undefined,
        };

        const envelopeMessages = data.envelope_messages;
        if (
          data.is_envelope === true &&
          Array.isArray(envelopeMessages) &&
          envelopeMessages.length > 0
        ) {
          // Persist a stub Message so storage / unread count stay coherent
          // with the legacy markdown path.
          //
          // When the WS already rendered the streamed text, drop the
          // leading text message from envelope_messages so the same
          // paragraph isn't shown twice. We only filter the FIRST
          // message — any subsequent text bubbles come from tool
          // results and must still render.
          let messagesToRender = envelopeMessages;
          if (
            streamingAlreadyRendered &&
            Array.isArray(messagesToRender) &&
            messagesToRender.length > 0 &&
            (messagesToRender[0] as any)?.type === 'text'
          ) {
            messagesToRender = messagesToRender.slice(1);
          }
          if (!streamingAlreadyRendered) {
            this.state.messages.push(assistantMessage);
            this.storage.setMessages(this.state.messages);
          }
          if (messagesToRender.length > 0) {
            this.renderEnvelopeMessages(messagesToRender, data.actions || []);
          }
          this.scrollToBottom();
          if (!this.state.isOpen) {
            this.state.unreadCount++;
            this.updateBadge();
          }
        } else if (!streamingAlreadyRendered) {
          this.addMessage(assistantMessage);
        }

        // Notify callback
        this.config.onMessage(assistantMessage);

        // Try to attach realtime now that conversation/cart ids may exist.
        if (this.state.isOpen) this.ensureRealtime();
      } else {
        // Error message
        this.addMessage({
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: 'Lo siento, hubo un problema. ¿Podés intentar de nuevo?',
          timestamp: new Date(),
          type: 'text',
        });
        this.config.onError(new Error(response.error || 'Unknown error'));
      }
    } catch (error) {
      this.hideTyping();
      console.error('[LumiariChat] Send error:', error);
      this.errorReporter?.captureException(error, { source: 'sendMessage' });
      this.addMessage({
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: 'Error de conexión. Verificá tu internet e intentá de nuevo.',
        timestamp: new Date(),
        type: 'text',
      });
      this.config.onError(error as Error);
    } finally {
      this.state.isLoading = false;
      this.flushQueuedCheckout();
    }
  }

  public setUser(user: { name?: string; email?: string; phone?: string }): void {
    this.state.user = user;
    this.storage.setUser(user);
  }

  public destroy(): void {
    this.teardownRealtime();
    this.closeIframe();
    // Tear down observability listeners + flush. Fire-and-forget; never
    // block teardown on the network.
    if (this.errorReporter) {
      void this.errorReporter.destroy();
      this.errorReporter = null;
    }
    this.container.remove();
  }

  /**
   * Observability (W2-1). Initialize gated error reporting. The Sentry SDK
   * is only loaded (lazily, via the host browser) when a DSN is present —
   * keeping the shipped bundle unchanged. Fully fail-closed.
   */
  private initErrorReporting(): void {
    try {
      const opts = this.config.errorReporting || {};
      const dsn = resolveDsn(opts.dsn ?? null);
      if (!dsn) return; // master gate closed — do nothing.

      this.errorReporter = new ErrorReporter({
        dsn,
        release: opts.release || '2.0.0',
        environment: opts.environment ?? null,
        sampleRate: opts.sampleRate,
        cdnUrl: opts.cdnUrl,
        // Identifier-shaped tags only — never PII / message content.
        tags: {
          widget_key: this.config.widgetKey || null,
          agent_id: this.config.agentId || null,
          store_id: this.config.storeId || null,
        },
      });
      // Lazy SDK load + listener install — fire-and-forget.
      void this.errorReporter.start();
    } catch {
      // Observability must never break widget construction.
      this.errorReporter = null;
    }
  }

  // =========================================
  // AGENT OPENING GREETING (A3b)
  // =========================================

  /**
   * Fetch + render the agent's opening greeting on first open (A3b).
   *
   * The agent always greets first. We call the opening endpoint at most once
   * per session, and only when:
   *   - a widget key is configured (the endpoint requires widget auth), AND
   *   - the conversation is still empty (no prior turns — don't re-greet a
   *     resumed or already-active conversation), AND
   *   - it hasn't already been requested this session.
   *
   * Fully fail-safe: a non-200, an empty body, or any transport error
   * renders nothing and logs — it never blocks the widget.
   */
  private async maybeFetchOpening(): Promise<void> {
    if (this.openingRequested) return;
    if (!this.config.widgetKey) return;
    // Only greet a genuinely fresh conversation: no server conversation id
    // and no real prior turns. The synthetic `welcome` stub (added at
    // construction) does NOT count as a real turn — the opening replaces it.
    if (this.storage.getConversationId()) return;
    if (!this.isFreshConversation()) return;
    this.openingRequested = true;

    try {
      const res = await this.api.getOpening();
      if (!res.success || !res.data) return; // none / error ⇒ render nothing.
      const messages = Array.isArray(res.data.messages) ? res.data.messages : [];
      if (messages.length === 0) return;
      // Re-check freshness (a race where the shopper typed before the
      // opening resolved): if they already spoke, don't prepend a greeting.
      if (!this.isFreshConversation()) return;

      // Replace the synthetic welcome stub with the real agent greeting so
      // the shopper never sees two greetings.
      this.removeWelcomeStub();
      this.renderEnvelopeMessages(messages as EnvelopeMessage[], []);

      const text = messages
        .map((m) => (m && typeof m.content === 'string' ? m.content : ''))
        .filter(Boolean)
        .join('\n\n');
      const greeting: Message = {
        id: `opening_${Date.now()}`,
        role: 'assistant',
        content: text,
        timestamp: new Date(),
        type: 'text',
        metadata: { agent_opening: true },
      };
      this.state.messages.push(greeting);
      this.storage.setMessages(this.state.messages);
      this.config.onMessage(greeting);
      this.scrollToBottom();
    } catch (error) {
      // Opening must never break the widget.
      console.warn('[LumiariChat] opening fetch failed', error);
    }
  }

  /**
   * A conversation is "fresh" for the opening greeting when it has no
   * messages, or only the synthetic `welcome` stub. Any real turn means the
   * shopper already has context and the agent must not re-greet.
   */
  private isFreshConversation(): boolean {
    const msgs = this.state.messages;
    if (msgs.length === 0) return true;
    return msgs.length === 1 && msgs[0]?.id === 'welcome';
  }

  /**
   * Remove the synthetic `welcome` bubble from state + DOM (called when the
   * server-side opening greeting replaces it).
   */
  private removeWelcomeStub(): void {
    const idx = this.state.messages.findIndex((m) => m.id === 'welcome');
    if (idx === -1) return;
    this.state.messages.splice(idx, 1);
    this.storage.setMessages(this.state.messages);
    // Re-render the (now welcome-free) message list so the DOM matches.
    this.renderMessages();
  }

  // =========================================
  // PRIVATE METHODS
  // =========================================

  /** @internal */
  addMessage(message: Message): void {
    this.state.messages.push(message);
    this.storage.setMessages(this.state.messages);
    this.renderMessage(message);
    this.scrollToBottom();

    // Update unread count if closed
    if (!this.state.isOpen && message.role === 'assistant') {
      this.state.unreadCount++;
      this.updateBadge();
    }
  }

  /** @internal */
  renderMessages(): void {
    this.messagesContainer.innerHTML = '';
    this.state.messages.forEach((msg) => this.renderMessage(msg));
    this.scrollToBottom();
  }

  private renderMessage(message: Message): void {
    const messageEl = document.createElement('div');
    messageEl.className = `lc-message ${message.role}`;

    const bubbleEl = document.createElement('div');
    bubbleEl.className = 'lc-message-bubble';

    // v2: server emits structured envelopes. The legacy text bubble path
    // only escapes content + converts newlines. innerHTML is safe because
    // formatMessageContent escapes all input.
    bubbleEl.innerHTML = this.formatMessageContent(message.content);
    messageEl.appendChild(bubbleEl);

    // Render product carousel only when products arrive on the API
    // payload directly (legacy ChatResponse path). Envelope-driven
    // product_grid messages render via the Preact MessageList in
    // `renderEnvelopeMessages()`.
    const productsToRender = message.products || [];
    if (productsToRender.length > 0 && this.config.features.productCards) {
      const carouselEl = this.renderProductCarousel(productsToRender);
      messageEl.appendChild(carouselEl);
    }

    // Add quick replies if present
    if (message.options && message.options.length > 0 && this.config.features.quickReplies) {
      const repliesEl = document.createElement('div');
      repliesEl.className = 'lc-quick-replies';

      message.options.forEach((option) => {
        const btn = document.createElement('button');
        btn.className = 'lc-quick-reply';
        btn.textContent = option.title;
        btn.onclick = () => this.sendMessage(option.title);
        repliesEl.appendChild(btn);
      });

      messageEl.appendChild(repliesEl);
    }

    // The editorial thread does not surface per-message timestamps.
    this.messagesContainer.appendChild(messageEl);
  }

  /**
   * Render a legacy `products[]` carousel. v2 NOTE: structured envelope
   * `product_grid` messages are the canonical path and render via Preact
   * `MessageList`. This minimal vanilla fallback exists only for hosts
   * still running chat_ms responses with raw `products` on `ChatResponse`.
   * It uses the same Preact ProductGrid component to avoid duplicating
   * carousel logic.
   */
  private renderProductCarousel(
    products: Array<{ id: string; name: string; price: number; price_formatted?: string; image?: string; url?: string }>,
  ): HTMLElement {
    const host = document.createElement('div');
    host.className = 'lc-envelope-host';
    const productCards: any[] = products.map((p) => ({
      product_id: p.id,
      name: p.name,
      price: p.price,
      image_url: p.image,
      url: p.url,
    }));
    const message = {
      role: 'assistant' as const,
      type: 'product_grid' as const,
      data: { products: productCards },
    };
    const onAction: ActionHandler = (intent) => this.handleWidgetIntent(intent);
    preactRender(
      h(MessageList as any, {
        messages: [message] as any,
        actions: [],
        onAction,
        onOpenCheckout: () => {},
      }),
      host,
    );
    return host;
  }

  /**
   * v2 NOTE: the markdown extractor + carousel-placeholder logic was
   * deleted in feat/widget-v2-preact-production. Server-side `chat_ms`
   * now emits structured `ChatResponseEnvelope` payloads — products,
   * carts, checkout links and order statuses arrive as typed
   * `EnvelopeMessage`s rendered by Preact components in
   * `src/components/v2/`. The widget no longer parses LLM markdown for
   * structured data. The HTTP envelope path goes through
   * `renderEnvelopeMessages()`; only the legacy plain-text message bubble
   * (no envelope) goes through `formatMessageContent()` below, which
   * just escapes HTML and converts newlines to <br>.
   *
   * XSS surface: the only HTML produced here is `<br>` and the escaped
   * text. No interpolation of server URLs or markdown structure.
   */
  private formatMessageContent(content: string): string {
    return this.escapeHtml(content).replace(/\n/g, '<br>');
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }

  private sanitizeUrl(value: string): string {
    return sharedSanitizeUrl(value);
  }

  private showTyping(): void {
    const typingEl = document.createElement('div');
    typingEl.className = 'lc-message assistant';
    typingEl.id = 'lc-typing';
    typingEl.innerHTML = `
      <div class="lc-typing">
        <span class="lc-typing-dot"></span>
        <span class="lc-typing-dot"></span>
        <span class="lc-typing-dot"></span>
      </div>
    `;
    this.messagesContainer.appendChild(typingEl);
    this.scrollToBottom();
  }

  private hideTyping(): void {
    const typingEl = this.messagesContainer.querySelector('#lc-typing');
    typingEl?.remove();
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    });
  }

  private updateBadge(): void {
    const existingBadge = this.launcher.querySelector('.lc-launcher-badge');

    if (this.state.unreadCount > 0) {
      if (existingBadge) {
        existingBadge.textContent = String(this.state.unreadCount);
      } else {
        const badge = document.createElement('span');
        badge.className = 'lc-launcher-badge';
        badge.textContent = String(this.state.unreadCount);
        this.launcher.appendChild(badge);
      }
    } else {
      existingBadge?.remove();
    }
  }

  private updateCart(cart: CartState): void {
    this.state.cart = cart;
    this.storage.setCart({ items: cart.items, total: cart.total });

    if (this.cartPreview && cart.itemCount > 0) {
      this.cartPreview.style.display = 'flex';
      const countEl = this.cartPreview.querySelector('.lc-cart-count');
      const totalEl = this.cartPreview.querySelector('.lc-cart-total');
      if (countEl) countEl.textContent = String(cart.itemCount);
      if (totalEl) totalEl.textContent = `$${cart.total.toLocaleString('es-AR')}`;
    } else if (this.cartPreview) {
      this.cartPreview.style.display = 'none';
    }

    // Header cart icon badge (persistent access, next to history).
    const badge = this.window.querySelector('.lc-header-cart-badge') as HTMLElement | null;
    if (badge) {
      if (cart.itemCount > 0) {
        badge.textContent = String(cart.itemCount);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    // Also update cart panel if visible
    if (this.state.showCartPanel) {
      this.renderCartPanel();
    }

    this.config.onCartUpdate(cart);
  }

  private flushQueuedCheckout(): void {
    if (!this.checkoutQueued) return;
    this.checkoutQueued = false;
    this.initiateCheckout();
  }

  private initiateCheckout(): void {
    // dispatchToolIntent drops intents while a request is in flight; a
    // checkout click must never be lost: run it when that request ends.
    // A second click while one is starting is the same checkout.
    if (this.checkoutInFlight) return;
    if (this.state.isLoading) {
      this.checkoutQueued = true;
      return;
    }
    this.checkoutInFlight = true;
    void this.dispatchToolIntent({
      tool: 'checkout.start',
      args: { cart_id: this.storage.getCartId() || undefined },
      args_schema_ref: 'checkout.start',
      idempotency_key: `checkout_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    }).finally(() => {
      this.checkoutInFlight = false;
    });
  }

  // =========================================
  // ENVELOPE: Preact rendering layer
  // =========================================

  /**
   * Mount a Preact tree for the typed envelope `messages[]`. Each Preact
   * subtree owns its own host `<div class="lc-envelope-host">` inside the
   * messages container, so the host element is the unit we discard
   * during `destroy()` (the parent container.remove() takes them all).
   *
   * @param messages typed envelope messages (text, product_grid, ...)
   * @param actions  envelope-level action buttons (tool shortcuts)
   */
  private renderEnvelopeMessages(
    messages: EnvelopeMessage[],
    actions: EnvelopeAction[],
  ): void {
    const host = document.createElement('div');
    host.className = 'lc-envelope-host';
    this.messagesContainer.appendChild(host);

    const onAction: ActionHandler = (intent) => this.handleWidgetIntent(intent);
    const onOpenCheckout = () => {
      if (this.lastCheckoutEmbed) {
        this.openCheckoutIframe(this.lastCheckoutEmbed);
      }
    };

    preactRender(
      h(MessageList, {
        messages,
        actions,
        onAction,
        onOpenCheckout,
        // With features.cart the cart bar (or the open panel) carries the
        // one "Finalizar compra"; cart cards in the thread do not repeat it.
        cartCheckout: !this.config.features.cart,
      }),
      host,
    );
  }

  /**
   * Render the product detail view as a fresh assistant turn. Triggered by
   * a `view-product` intent from the editorial carousel. Self-contained
   * from the ProductCardData already in hand — no backend round-trip — so
   * the envelope contract with chat_ms is untouched. The detail CTA emits
   * the existing add-to-cart intent.
   */
  private renderProductDetail(product: ProductCardData): void {
    const host = document.createElement('div');
    host.className = 'lc-envelope-host';
    this.messagesContainer.appendChild(host);
    const onAction: ActionHandler = (intent) => this.handleWidgetIntent(intent);
    const message: EnvelopeMessage = {
      role: 'assistant',
      type: 'product_detail',
      data: { product },
    };
    preactRender(
      h(MessageList, {
        messages: [message],
        actions: [],
        onAction,
        onOpenCheckout: () => {},
      }),
      host,
    );
    this.scrollToBottom();
  }

  /**
   * Translate a structured WidgetIntent emitted from a Preact component
   * into the corresponding side-effect.
   *
   * Emits structured tool_intent bodies. The browser never sends tenant
   * identifiers; chat_ms resolves tenancy from the widget key/session JWT.
   */
  private handleWidgetIntent(intent: WidgetIntent): void {
    switch (intent.kind) {
      case 'add-to-cart': {
        const product = intent.product;
        if (!product?.product_id) return;
        this.dispatchToolIntent({
          tool: 'cart.add_item',
          args: {
            product_id: product.product_id,
            variant_id: product.variant_id || undefined,
            name: product.name,
            price: product.price,
            currency: product.currency,
            image_url: product.image_url || undefined,
            quantity: 1,
          },
          args_schema_ref: 'cart.add_item',
        });
        break;
      }
      case 'remove-from-cart': {
        if (!intent.itemId) return;
        this.dispatchToolIntent({
          tool: 'cart.remove_item',
          args: { item_id: intent.itemId },
          args_schema_ref: 'cart.remove_item',
        });
        break;
      }
      case 'update-quantity': {
        if (!intent.itemId) break;
        const current = this.state.cart.items.find((item) => item.id === intent.itemId);
        const quantity = Math.max(0, (current?.quantity || 1) + intent.delta);
        if (quantity <= 0) {
          this.dispatchToolIntent({
            tool: 'cart.remove_item',
            args: { item_id: intent.itemId },
            args_schema_ref: 'cart.remove_item',
          });
        } else {
          this.dispatchToolIntent({
            tool: 'cart.update_item',
            args: { item_id: intent.itemId, quantity },
            args_schema_ref: 'cart.update_item',
          });
        }
        break;
      }
      case 'view-product': {
        // Editorial carousel has no inline add button — tapping a card
        // opens the product detail view (a fresh turn in the thread) whose
        // CTA emits the add-to-cart intent above.
        if (intent.product) this.renderProductDetail(intent.product);
        break;
      }
      case 'open-checkout': {
        this.initiateCheckout();
        break;
      }
      case 'envelope-action': {
        const action = intent.action;
        if (!action || !action.tool) return;
        this.dispatchToolIntent({
          tool: action.tool,
          args: action.args || {},
          args_schema_ref: action.args_schema_ref,
          client_dispatch_id: action.id,
        });
        break;
      }
      case 'send-reply': {
        // WhatsApp quick-reply / list-row pick: re-send the option's reply
        // string as the shopper's next chat message.
        const text = (intent.text || '').trim();
        if (!text) break;
        void this.sendMessage(text);
        break;
      }
    }
  }

  private async dispatchToolIntent(intent: {
    tool?: string;
    type?: string;
    args?: Record<string, any>;
    payload?: Record<string, any>;
    args_schema_ref?: string;
    client_dispatch_id?: string;
    idempotency_key?: string;
  }): Promise<void> {
    if (this.state.isLoading) return;
    this.showTyping();
    this.state.isLoading = true;
    try {
      const response = await this.api.sendToolIntent({
        ...intent,
        client_dispatch_id:
          intent.client_dispatch_id ||
          intent.idempotency_key ||
          `wd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      });
      this.hideTyping();
      if (response.success && response.data) {
        this.applyNormalizedPayload(response.data);
      } else {
        this.addMessage({
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: 'No pude completar esa acción. Probá de nuevo.',
          timestamp: new Date(),
          type: 'text',
        });
        this.config.onError(new Error(response.error || 'Tool intent failed'));
      }
    } catch (error) {
      this.hideTyping();
      this.addMessage({
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: 'Error de conexión. Verificá tu internet e intentá de nuevo.',
        timestamp: new Date(),
        type: 'text',
      });
      this.errorReporter?.captureException(error, {
        source: 'dispatchToolIntent',
        tool: intent.tool || intent.type || null,
      });
      this.config.onError(error as Error);
    } finally {
      this.state.isLoading = false;
      this.flushQueuedCheckout();
    }
  }

  private applyNormalizedPayload(data: NormalizedChatPayload): void {
    if (data.conversation_id) {
      this.storage.setConversationId(data.conversation_id);
      this.api.setConversationId(data.conversation_id);
      this.state.phase = data.phase || this.state.phase;
    }
    if (data.session_id) {
      this.state.sessionId = data.session_id;
      this.storage.setSessionId(data.session_id);
    }
    this.applyRequiresHuman(!!data.requires_human);
    if (data.cart) {
      this.updateCartFromEnvelope(data.cart);
    } else if (data.cart_items && data.cart_items.length > 0) {
      this.updateCart({
        items: data.cart_items,
        total: data.cart_total || 0,
        itemCount: data.cart_items.length,
      });
    }
    if (data.checkout_embed) {
      this.lastCheckoutEmbed = data.checkout_embed;
      this.openCheckoutIframe(data.checkout_embed);
    }
    if (data.login_request && !this.shopperUser) {
      this.toggleLogin(true);
    }
    if (data.wallet_add_card) {
      this.openWalletIframe(data.wallet_add_card);
      if ((data.wallet_add_card as any).organization_id) {
        this.realtimeOrganizationId = String(
          (data.wallet_add_card as any).organization_id,
        );
      }
    }
    this.applyRealtimeToken(data.realtime_token);

    const assistantMessage: Message = {
      id: `msg_${Date.now()}`,
      role: 'assistant',
      content: data.response,
      timestamp: new Date(),
      type: data.response_type || 'text',
      options: data.response_options,
      products: data.products || [],
      metadata: data.order_status ? { order_status: data.order_status } : undefined,
    };

    const envelopeMessages = data.envelope_messages;
    if (
      data.is_envelope === true &&
      Array.isArray(envelopeMessages) &&
      envelopeMessages.length > 0
    ) {
      this.state.messages.push(assistantMessage);
      this.storage.setMessages(this.state.messages);
      this.renderEnvelopeMessages(envelopeMessages, data.actions || []);
      this.scrollToBottom();
      if (!this.state.isOpen) {
        this.state.unreadCount++;
        this.updateBadge();
      }
    } else {
      this.addMessage(assistantMessage);
    }

    this.config.onMessage(assistantMessage);
    if (this.state.isOpen) this.ensureRealtime();
  }

  // =========================================
  // ENVELOPE: structured payload renderers
  // =========================================

  /** Map ChatEnvelope cart_summary -> internal CartState and render. */
  private updateCartFromEnvelope(cart: CartSummaryData): void {
    if (!cart) return;
    if (cart.cart_id) {
      this.storage.setCartId(cart.cart_id);
    }
    const items = (cart.items || []).map((item) => ({
      id: item.item_id || item.product_id,
      productId: item.product_id,
      name: item.name || '',
      price: typeof item.unit_price === 'number' ? item.unit_price : 0,
      quantity: typeof item.quantity === 'number' ? item.quantity : 1,
      image: item.image_url || '',
    }));
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const total =
      cart.totals && typeof cart.totals.total === 'number'
        ? cart.totals.total
        : 0;
    this.updateCart({ items, total, itemCount });
  }

  /**
   * requires_human: disable the input + render a non-blocking banner.
   * Idempotent — repeated calls keep state in sync.
   */
  private applyRequiresHuman(active: boolean): void {
    this.requiresHuman = active;
    // `requires_human` significa "tus mensajes van a un humano", NO "no podés
    // escribir". Mantenemos el input HABILITADO: el comprador sigue escribiendo y
    // el backend rutea sus mensajes al asesor (loop de handoff), devolviendo las
    // respuestas del humano. Antes se deshabilitaba el input y —sin carrito— ni
    // conectaba realtime → el comprador quedaba ATRAPADO (sin bot y sin humano).
    if (this.input) {
      this.input.classList.toggle('lc-input-handoff', active);
      if (active) {
        if (this.input.dataset.prevPlaceholder === undefined) {
          this.input.dataset.prevPlaceholder = this.input.placeholder;
        }
        this.input.placeholder = 'Escribí — un asesor te responde por acá';
      } else if (this.input.dataset.prevPlaceholder !== undefined) {
        this.input.placeholder = this.input.dataset.prevPlaceholder;
        delete this.input.dataset.prevPlaceholder;
      }
    }

    let banner = this.window.querySelector('.lc-handoff-banner') as HTMLElement | null;
    if (active) {
      if (!banner) {
        banner = document.createElement('div');
        banner.className = 'lc-handoff-banner';
        banner.textContent = 'Atendido por humano';
        // Insert above the input area
        const inputArea = this.window.querySelector('.lc-input-area');
        if (inputArea && inputArea.parentNode) {
          inputArea.parentNode.insertBefore(banner, inputArea);
        } else {
          this.window.appendChild(banner);
        }
      }
    } else if (banner) {
      banner.remove();
    }
  }

  // =========================================
  // ENVELOPE: checkout / wallet iframes
  // =========================================

  /**
   * Open the embedded checkout in a bottom-sheet iframe.
   * Listens for postMessage events ONLY from the configured checkout
   * origin (default https://checkout.lumiari.ai). Messages from other
   * origins are dropped silently — this is the security boundary.
   */
  public openCheckoutIframe(embed: CheckoutEmbedData): void {
    if (!embed || !embed.checkout_url) return;
    const safeUrl = this.sanitizeUrl(embed.checkout_url);
    if (!safeUrl) {
      console.warn('[LumiariChat] Rejected checkout_url (unsafe scheme).');
      return;
    }
    if (!this.isAllowedCheckoutUrl(safeUrl)) {
      console.warn('[LumiariChat] Rejected checkout_url (origin mismatch).');
      return;
    }
    this.closeIframe(); // ensure only one iframe at a time

    // Hosts outside *.lumiari.ai cannot frame the checkout (CSP frame-ancestors):
    // open it in a new tab instead, with an in-chat button as fallback.
    if (checkoutPresentation(getEmbedHostname()) === 'tab') {
      this.openCheckoutInTab(safeUrl);
      return;
    }

    const providerLabel = this.getCheckoutProviderLabel(embed);
    const sheet = this.buildIframeSheet('lc-checkout-sheet', {
      title:
        providerLabel === 'Mercado Pago'
          ? 'Checkout seguro con Mercado Pago'
          : 'Checkout seguro',
      description:
        providerLabel === 'Mercado Pago'
          ? 'Tus datos de pago se cargan en Mercado Pago. Si la compra requiere validacion, abrimos una pestana protegida.'
          : 'Tus datos de pago se cargan en una ventana protegida. Te avisamos cuando la orden cambie de estado.',
      status: 'Esperando confirmacion del pago...',
    });
    const iframe = document.createElement('iframe');
    iframe.className = 'lc-checkout-iframe';
    iframe.src = safeUrl;
    iframe.setAttribute('title', 'Checkout');
    iframe.setAttribute('allow', 'payment *');
    sheet.body.appendChild(iframe);

    this.currentIframe = iframe;

    const expectedOrigin = this.config.checkoutOrigin;

    const handler = (event: MessageEvent) => {
      // ORIGIN PINNING — non-negotiable.
      if (event.origin !== expectedOrigin) {
        return;
      }
      // The source must be our iframe.
      if (event.source !== iframe.contentWindow) {
        return;
      }
      const payload = event.data || {};
      const type: string | undefined = payload.type;
      if (!type) return;

      switch (type) {
        case CHECKOUT_READY: {
          // Send PREFILL with available shopper context
          const prefill: Record<string, any> = {};
          if (embed.shopper_token) prefill.shopper_token = embed.shopper_token;
          if (embed.cart_id) prefill.cart_id = embed.cart_id;
          if (this.state.user?.email) prefill.email = this.state.user.email;
          if (this.state.user?.name) prefill.name = this.state.user.name;
          if (this.state.user?.phone) prefill.phone = this.state.user.phone;
          this.postToIframe({ type: PREFILL, data: prefill });
          break;
        }
        case OPEN_PAYMENT_URL: {
          const targetUrl = this.sanitizeUrl(String(payload.url || ''));
          if (targetUrl) {
            this.setCheckoutStatusText('Abrimos la validacion del pago en una pestana segura.');
            window.open(targetUrl, '_blank', 'noopener,noreferrer');
          }
          break;
        }
        case CHECKOUT_STATUS: {
          const order = this.extractOrderStatus(payload);
          if (order) {
            this.setCheckoutStatusFromOrder(order);
            if (this.isTerminalPaymentStatus(order.payment_status)) {
              this.closeIframe();
              this.renderOrderStatus(order);
            }
          } else if (typeof payload.status === 'string' && payload.status) {
            this.setCheckoutStatusText(String(payload.status));
          }
          break;
        }
        case CHECKOUT_COMPLETE: {
          const order = this.extractOrderStatus(payload);
          this.closeIframe();
          if (order && order.order_id) {
            this.renderOrderStatus(order);
          }
          break;
        }
        case CHECKOUT_CLOSE: {
          this.closeIframe();
          break;
        }
      }
    };

    this.iframeMessageHandler = handler;
    window.addEventListener('message', handler);
    this.startCheckoutStatusPolling(embed);
  }

  /**
   * Open an already origin-validated checkout URL in a new tab and render a
   * "Pay" button in the chat as a fallback for blocked popups. `noopener` is
   * applied by nulling `opener` (passing it as a feature makes `window.open`
   * always return null, which would hide a blocked popup).
   */
  private openCheckoutInTab(url: string): void {
    let opened: Window | null = null;
    try {
      opened = window.open(url, '_blank');
      if (opened) opened.opener = null;
    } catch {
      opened = null;
    }
    this.renderCheckoutFallbackButton(url, !opened);
  }

  private renderCheckoutFallbackButton(url: string, popupBlocked: boolean): void {
    if (!this.messagesContainer) return;
    const wrap = document.createElement('div');
    wrap.className = 'lc-message assistant lc-checkout-fallback';
    const bubble = document.createElement('div');
    bubble.className = 'lc-message-bubble';
    if (popupBlocked) {
      const note = document.createElement('div');
      note.textContent = t('checkout.popupBlocked');
      bubble.appendChild(note);
    }
    const link = document.createElement('a');
    link.className = 'lc-checkout-pay-button';
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = t('checkout.pay');
    bubble.appendChild(link);
    wrap.appendChild(bubble);
    this.messagesContainer.appendChild(wrap);
    this.scrollToBottom();
  }

  /**
   * Open the wallet add-card flow in an iframe (mode=wallet-add-card).
   * Same origin pinning as the checkout iframe.
   */
  public openWalletIframe(walletData: WalletAddCardData): void {
    if (!walletData) return;
    const url = walletData.checkout_url
      ? this.sanitizeUrl(walletData.checkout_url)
      : `${this.config.checkoutOrigin}/?mode=wallet-add-card`;
    if (!url) return;
    if (!this.isAllowedCheckoutUrl(url)) {
      console.warn('[LumiariChat] Rejected wallet checkout_url (origin mismatch).');
      return;
    }

    this.closeIframe();

    if (checkoutPresentation(getEmbedHostname()) === 'tab') {
      this.openCheckoutInTab(url);
      return;
    }

    const sheet = this.buildIframeSheet('lc-wallet-sheet', {
      title: t('wallet.addPaymentMethod'),
      description: t('wallet.secureCardNote'),
      status: 'Esperando el formulario...',
    });
    const iframe = document.createElement('iframe');
    iframe.className = 'lc-wallet-iframe';
    iframe.src = url;
    iframe.setAttribute('title', 'Add card');
    sheet.body.appendChild(iframe);

    this.currentIframe = iframe;

    const expectedOrigin = this.config.checkoutOrigin;

    const handler = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframe.contentWindow) return;
      const payload = event.data || {};
      const type: string | undefined = payload.type;
      if (!type) return;

      switch (type) {
        case WALLET_READY: {
          this.setCheckoutStatusText('Formulario listo.');
          const prefill: Record<string, any> = {};
          if (walletData.organization_id) prefill.organization_id = walletData.organization_id;
          if (walletData.provider_id) prefill.provider_id = walletData.provider_id;
          if (walletData.public_key) prefill.public_key = walletData.public_key;
          if (walletData.shopper_token) prefill.shopper_token = walletData.shopper_token;
          if (this.state.user?.email) prefill.email = this.state.user.email;
          this.postToIframe({ type: WALLET_PREFILL, data: prefill });
          break;
        }
        case CARD_SAVED: {
          this.closeIframe();
          break;
        }
        case CARD_ERROR: {
          // Surface non-fatally: leave the iframe so user can retry.
          this.setCheckoutStatusText('No pudimos guardar la tarjeta. Revisá los datos e intentá de nuevo.');
          console.warn('[LumiariChat] Wallet card error:', payload.error);
          break;
        }
        case WALLET_CLOSE: {
          this.closeIframe();
          break;
        }
      }
    };

    this.iframeMessageHandler = handler;
    window.addEventListener('message', handler);
  }

  /** Build a bottom-sheet container in the Shadow DOM and return it. */
  private buildIframeSheet(
    className: string,
    copy?: { title?: string; description?: string; status?: string },
  ): { sheet: HTMLDivElement; body: HTMLDivElement } {
    if (!this.iframeContainer) {
      this.iframeContainer = document.createElement('div');
      this.iframeContainer.className = 'lc-iframe-container';
      this.window.appendChild(this.iframeContainer);
    }

    const sheet = document.createElement('div');
    sheet.className = `lc-iframe-sheet ${className}`;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'lc-iframe-close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.textContent = '×'; // ×
    closeBtn.addEventListener('click', () => this.closeIframe());
    sheet.appendChild(closeBtn);

    const header = document.createElement('div');
    header.className = 'lc-iframe-header';

    const title = document.createElement('div');
    title.className = 'lc-iframe-title';
    title.textContent = copy?.title || 'Checkout seguro';
    header.appendChild(title);

    const description = document.createElement('div');
    description.className = 'lc-iframe-description';
    description.textContent =
      copy?.description ||
      'La operacion continua en una ventana protegida.';
    header.appendChild(description);

    const status = document.createElement('div');
    status.className = 'lc-iframe-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = copy?.status || 'Conectando...';
    header.appendChild(status);

    sheet.appendChild(header);

    const body = document.createElement('div');
    body.className = 'lc-iframe-body';
    sheet.appendChild(body);

    this.iframeContainer.innerHTML = '';
    this.iframeContainer.appendChild(sheet);
    return { sheet, body };
  }

  private postToIframe(payload: Record<string, any>): void {
    if (!this.currentIframe || !this.currentIframe.contentWindow) return;
    try {
      this.currentIframe.contentWindow.postMessage(payload, this.config.checkoutOrigin);
    } catch (err) {
      console.error('[LumiariChat] postMessage to iframe failed:', err);
    }
  }

  private isAllowedCheckoutUrl(value: string): boolean {
    try {
      const base =
        typeof window !== 'undefined' && window.location
          ? window.location.origin
          : this.config.checkoutOrigin;
      return new URL(value, base).origin === this.config.checkoutOrigin;
    } catch {
      return false;
    }
  }

  private getCheckoutProviderLabel(embed: Partial<CheckoutEmbedData>): string {
    const raw = String(embed.payment_provider || embed.provider || '').toLowerCase();
    if (raw.includes('mercado') || raw.includes('mp')) return 'Mercado Pago';
    return '';
  }

  private setCheckoutStatusText(text: string): void {
    const status = this.window.querySelector('.lc-iframe-status');
    if (status) status.textContent = text;
  }

  private setCheckoutStatusFromOrder(order: OrderStatusData): void {
    const labels: Record<string, string> = {
      paid: 'Pago confirmado. Cerrando checkout...',
      pending: 'Pago pendiente. Seguimos esperando la confirmacion.',
      failed: 'Pago rechazado. Podés intentar con otro medio de pago.',
      expired: 'Pago expirado. Iniciá un nuevo checkout para continuar.',
      refunded: 'Pago reembolsado.',
    };
    this.setCheckoutStatusText(
      labels[order.payment_status] || `Estado del pago: ${order.payment_status}`,
    );
  }

  private isTerminalPaymentStatus(status: string | null | undefined): boolean {
    return status === 'paid' || status === 'failed' || status === 'expired' || status === 'refunded';
  }

  private extractOrderStatus(payload: Record<string, any> | undefined): OrderStatusData | null {
    if (!payload || typeof payload !== 'object') return null;
    const candidate =
      (payload.order && typeof payload.order === 'object' ? payload.order : null) ||
      (payload.data?.order && typeof payload.data.order === 'object' ? payload.data.order : null) ||
      (payload.data && typeof payload.data === 'object' ? payload.data : null) ||
      payload;

    const orderId = candidate.order_id || candidate.id;
    const paymentStatus = candidate.payment_status || candidate.status;
    if (!orderId || !paymentStatus) return null;

    const total = candidate.total;
    return {
      order_id: String(orderId),
      order_number: candidate.order_number ? String(candidate.order_number) : undefined,
      payment_status: String(paymentStatus) as OrderStatusData['payment_status'],
      fulfillment_status: candidate.fulfillment_status
        ? String(candidate.fulfillment_status)
        : undefined,
      total: typeof total === 'number' ? total : null,
      currency: candidate.currency ? String(candidate.currency) : null,
    };
  }

  private startCheckoutStatusPolling(embed: CheckoutEmbedData): void {
    this.stopCheckoutStatusPolling();
    const statusUrl = this.sanitizeUrl(embed.status_url || '');
    if (!statusUrl || !this.isAllowedCheckoutUrl(statusUrl)) return;

    const configuredInterval =
      typeof embed.poll_interval_ms === 'number' ? embed.poll_interval_ms : 2500;
    const baseInterval = Math.min(Math.max(configuredInterval, 1500), 10000);

    const poll = async () => {
      if (!this.currentIframe) return;
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.checkoutPollAbort = controller;

      try {
        const response = await fetch(statusUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'omit',
          signal: controller?.signal,
        });
        if (!this.currentIframe) return;
        if (response.ok) {
          const payload = await response.json().catch(() => ({}));
          const order = this.extractOrderStatus(payload);
          if (order) {
            this.checkoutPollFailures = 0;
            this.setCheckoutStatusFromOrder(order);
            if (this.isTerminalPaymentStatus(order.payment_status)) {
              this.closeIframe();
              this.renderOrderStatus(order);
              return;
            }
          }
        }
      } catch (err) {
        if (!this.currentIframe) return;
        this.checkoutPollFailures += 1;
        this.setCheckoutStatusText('Reconectando con checkout...');
      } finally {
        if (this.checkoutPollAbort === controller) {
          this.checkoutPollAbort = null;
        }
      }

      if (!this.currentIframe) return;
      const multiplier = Math.min(this.checkoutPollFailures + 1, 4);
      this.checkoutPollTimer = setTimeout(poll, baseInterval * multiplier);
    };

    void poll();
  }

  private stopCheckoutStatusPolling(): void {
    if (this.checkoutPollTimer) {
      clearTimeout(this.checkoutPollTimer);
      this.checkoutPollTimer = null;
    }
    if (this.checkoutPollAbort) {
      try {
        this.checkoutPollAbort.abort();
      } catch {
        // ignore
      }
      this.checkoutPollAbort = null;
    }
    this.checkoutPollFailures = 0;
  }

  /** Tear down the active iframe + its message listener. */
  public closeIframe(): void {
    this.stopCheckoutStatusPolling();
    if (this.iframeMessageHandler) {
      window.removeEventListener('message', this.iframeMessageHandler);
      this.iframeMessageHandler = null;
    }
    this.currentIframe = null;
    // Remove the container, do not just empty it: it is a full-window,
    // z-index 220 overlay, and an empty one kept swallowing every click in
    // the widget after the first checkout sheet was closed.
    this.iframeContainer?.remove();
    this.iframeContainer = null;
  }

  /**
   * Render an order_status message as an assistant bubble. Used both by
   * the envelope renderer (when chat_ms includes an order_status message)
   * and by the checkout iframe CHECKOUT_COMPLETE handshake.
   */
  private renderOrderStatus(order: OrderStatusData): void {
    if (!order || !order.order_id) return;
    const statusLabels: Record<string, string> = {
      paid: 'Pago confirmado',
      pending: 'Pago pendiente',
      failed: 'Pago rechazado',
      expired: 'Pago expirado',
      refunded: 'Pago reembolsado',
    };
    const statusLabel = statusLabels[order.payment_status] || order.payment_status;
    const orderRef = order.order_number || order.order_id;
    const totalStr =
      typeof order.total === 'number'
        ? `${order.currency || ''} ${order.total.toLocaleString('es-AR')}`.trim()
        : '';
    const lines = [`Orden #${orderRef}`, statusLabel];
    if (totalStr) lines.push(`Total: ${totalStr}`);

    const message: Message = {
      id: `order_${order.order_id}_${Date.now()}`,
      role: 'assistant',
      content: lines.join('\n'),
      timestamp: new Date(),
      type: 'text',
      metadata: { order_status: order },
    };
    this.addMessage(message);
  }

  // =========================================
  // REALTIME HUB (cart / order / chat / handoff push)
  // =========================================

  private applyRealtimeToken(token?: string | null): void {
    if (!token) return;
    this.realtimeToken = token;
    const orgId = this.decodeJwtStringClaim(token, 'organization_id');
    if (orgId) {
      this.realtimeOrganizationId = orgId;
    }
  }

  private decodeJwtStringClaim(token: string, claim: string): string | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;
      const value = payload[claim];
      return typeof value === 'string' && value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }

  /**
   * Spin up RealtimeService once we have the tenant + cart scope. Idempotent:
   * skips if a service is already attached or required ids are missing.
   * Token: require the conversation-scoped JWT emitted by chat_ms.
   * The public widgetKey is not a realtime credential.
   */
  private ensureRealtime(): void {
    if (this.realtime) return;
    const conversationId = this.api.getConversationId() || this.storage.getConversationId();
    const cartId = this.storage.getCartId();
    const organizationId = this.realtimeOrganizationId;
    if (!conversationId || !cartId || !organizationId) return;
    if (!this.realtimeToken) return;

    const svc = new RealtimeService({
      url: this.config.realtimeUrl || undefined,
      events: [
        'cart.updated',
        'order.status',
        'chat.message_new',
        'handoff.assigned',
        // Streaming LLM frames — token_chunk grows the in-flight
        // assistant bubble, message_complete closes it.
        'conversation.token_chunk',
        'conversation.message_complete',
      ],
      filters: {
        organization_id: organizationId,
        conversation_id: conversationId,
        cart_id: cartId,
      },
      token: this.realtimeToken,
    });

    svc.on('cart.updated', (ev) => this.onRealtimeCartUpdated(ev));
    svc.on('order.status', (ev) => this.onRealtimeOrderStatus(ev));
    svc.on('chat.message_new', (ev) => this.onRealtimeChatMessage(ev));
    svc.on('handoff.assigned', (ev) => this.onRealtimeHandoffAssigned(ev));
    svc.on('conversation.token_chunk', (ev) => this.onRealtimeTokenChunk(ev));
    svc.on('conversation.message_complete', (ev) =>
      this.onRealtimeMessageComplete(ev),
    );

    svc.connect();
    this.realtime = svc;
  }

  private teardownRealtime(): void {
    if (!this.realtime) return;
    this.realtime.close();
    this.realtime = null;
  }

  private onRealtimeCartUpdated(event: RealtimeEvent): void {
    const payload = event.data as Partial<CartSummaryData> | undefined;
    if (!payload) return;
    // Reuse the envelope path so legacy + push share rendering.
    this.updateCartFromEnvelope(payload as CartSummaryData);
  }

  private onRealtimeOrderStatus(event: RealtimeEvent): void {
    const payload = event.data as OrderStatusData | undefined;
    if (!payload || !payload.order_id) return;
    this.renderOrderStatus(payload);
  }

  private onRealtimeChatMessage(event: RealtimeEvent): void {
    const payload = (event.data || {}) as {
      role?: string;
      content?: string;
      message_id?: string;
    };
    // Skip echoes of our own user turn — chat_ms publishes both user and
    // assistant frames; the widget already rendered the user one locally.
    if (payload.role === 'user') return;
    if (!payload.content) return;
    const id = payload.message_id || `rt_${Date.now()}`;
    if (this.state.messages.some((m) => m.id === id)) return; // de-dup
    this.addMessage({
      id,
      role: 'assistant',
      content: String(payload.content),
      timestamp: new Date(),
      type: 'text',
    });
  }

  private onRealtimeHandoffAssigned(event: RealtimeEvent): void {
    // Anything but a flat-out "unassigned" signal flips the human banner on.
    const payload = (event.data || {}) as { status?: string };
    const active = payload.status !== 'unassigned';
    this.applyRequiresHuman(active);
  }

  // =========================================
  // STREAMING (typewriter-style assistant bubble)
  // =========================================

  /**
   * `conversation.token_chunk` handler. Each frame carries:
   *   { conversation_id, delta, sequence }
   *
   * Behaviour:
   *   - First chunk for a conversation: hide the typing indicator,
   *     create a fresh assistant bubble and stamp the first delta.
   *   - Subsequent chunks: append the delta to the bubble's text node.
   *   - Out-of-order chunks: dropped upstream by RealtimeService.
   *
   * The bubble lives outside of the normal Message / envelope rendering
   * path — it's plain text only. When the matching HTTP envelope arrives
   * (or `conversation.message_complete` fires), we lock the bubble and
   * render any typed envelope contents AFTER it.
   */
  /** @internal */
  onRealtimeTokenChunk(event: RealtimeEvent): void {
    const data = (event.data || {}) as {
      conversation_id?: string;
      delta?: string;
      sequence?: number;
    };
    const delta = typeof data.delta === 'string' ? data.delta : '';
    if (!delta) return;
    const conv = String(data.conversation_id || '');

    if (!this.streamingBubbleEl) {
      // First chunk — open the bubble.
      this.hideTyping();
      this.streamingText = '';
      this.streamingCompleted = false;

      const messageEl = document.createElement('div');
      messageEl.className = 'lc-message assistant lc-message-streaming';

      const bubbleEl = document.createElement('div');
      bubbleEl.className = 'lc-message-bubble';

      const textEl = document.createElement('div');
      textEl.className = 'lc-streaming-text';
      bubbleEl.appendChild(textEl);
      messageEl.appendChild(bubbleEl);

      this.messagesContainer.appendChild(messageEl);
      this.streamingBubbleEl = messageEl;
      this.streamingTextEl = textEl;
    }

    this.streamingText += delta;
    if (this.streamingTextEl) {
      // Use textContent so any HTML in deltas is rendered as text — the
      // streaming bubble never trusts server-side markup.
      this.streamingTextEl.textContent = this.streamingText;
    }
    this.scrollToBottom();
  }

  /**
   * `conversation.message_complete` handler. Locks the streaming bubble
   * (no more deltas will append) and stamps the final text. Does NOT
   * remove the bubble — its text already has all the chunks. The HTTP
   * envelope arriving alongside will see `streamingCompleted=true` and
   * skip re-rendering the leading text message; structured envelope
   * messages (product_grid, cart_summary) render fresh AFTER the bubble.
   */
  /** @internal */
  onRealtimeMessageComplete(event: RealtimeEvent): void {
    const data = (event.data || {}) as {
      conversation_id?: string;
      text?: string;
    };
    if (!this.streamingBubbleEl) {
      // No in-flight bubble — nothing to lock. This can happen when the
      // HTTP envelope arrived first (race-loser); just clear pending
      // state so the next stream starts clean.
      this.streamingCompleted = true;
      this.streamingText = '';
      return;
    }
    if (this.streamingTextEl && typeof data.text === 'string' && data.text) {
      // Final text from the server may be slightly different from the
      // accumulated chunks (some providers re-emit the full text on done).
      // Use it as the source of truth for the locked bubble.
      this.streamingTextEl.textContent = data.text;
      this.streamingText = data.text;
    }
    this.streamingBubbleEl.classList.remove('lc-message-streaming');
    this.streamingBubbleEl.classList.add('lc-message-streaming-done');
    this.streamingCompleted = true;

    // Persist the streamed message so refresh / page re-open re-renders it.
    if (this.streamingText) {
      const persisted: Message = {
        id: `stream_${Date.now()}`,
        role: 'assistant',
        content: this.streamingText,
        timestamp: new Date(),
        type: 'text',
      };
      this.state.messages.push(persisted);
      this.storage.setMessages(this.state.messages);
    }
  }

  /**
   * Public hook so the HTTP `sendMessage` flow can ask "did the WS
   * already render this assistant turn's leading text?". When this
   * returns true, the HTTP path must skip the leading text message of
   * the envelope (otherwise the user sees the same paragraph twice).
   * Idempotent: subsequent envelopes/streams reset the state.
   */
  /** @internal */
  consumeStreamingCompletion(): boolean {
    const completed = this.streamingCompleted;
    this.streamingCompleted = false;
    this.streamingBubbleEl = null;
    this.streamingTextEl = null;
    this.streamingText = '';
    return completed;
  }
}
